import { useMemo, useState } from "react";
import { queries } from "../../../../shared/queries";
import { useGroupMemberships } from "../../../hooks/useGroupMemberships";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useQuery } from "../../../hooks/useQuery";
import { useSpaceDisplayName } from "../../../hooks/useSpaceDisplayName";
import { useUrlSort } from "../../../hooks/useUrlSort";
import { memberCountLabel } from "../../../lib/memberGroupMap";
import { spaceIcon } from "../../../lib/spaceIcon";
import { makeUrl, navigate, routes, useCurrentUrl } from "../../../lib/urls";
import { userDisplayName } from "../../../lib/userDisplayName";
import { Avatar } from "../../Avatar";
import { Button } from "../../Button";
import { EmptyState } from "../../EmptyState";
import { GroupMemberFacePile } from "../../GroupMemberFacePile";
import { Icon, type IconName } from "../../Icon";
import {
  type FilterDef,
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
} from "../../ListFilter";
import { ListTable } from "../../ListTable";
import { SpaceActionsMenu } from "../../SpaceActionsMenu";
import { SpaceSettings } from "../../SpaceSettings";
import { TabHeaderBar, Tabs } from "../../TabHeader";
import { TabLink } from "../../TabLink";
import { Text } from "../../Text";
import { Tooltip } from "../../Tooltip";
import {
  Container,
  HeaderActions,
  HoverActions,
  NeutralIconBadge,
  PillBadge,
  RowNameContent,
  RowNameLayout,
  ScrollArea,
  SearchField,
} from "../shared";
import {
  buildSpaceRows,
  countByTab,
  filterByManager,
  filterByMember,
  filterBySearch,
  filterByTab,
  isSpacesTab,
  sortSpaceRows,
  type SpacesTab,
} from "./lib";

const TABS: {
  id: SpacesTab;
  label: string;
  icon: IconName;
}[] = [
  { id: "all", label: "All spaces", icon: "hash" },
  { id: "member", label: "Member", icon: "user-round-check" },
  { id: "not-member", label: "Not a member", icon: "discoverable-space" },
  { id: "personal", label: "Personal", icon: "circle-user-round" },
];

export function SpacesSettings() {
  const [spaces, spacesResult] = useQuery(
    queries.spaces({ settingsView: true })
  );
  const [allUsers] = useQuery(queries.allUsers());
  const [allWorkflows] = useQuery(queries.allWorkflows());
  const [allPersonalChats] = useQuery(queries.allPersonalChats());
  const [currentUser] = useQuery(queries.currentUser());
  const [personalSpaceOwners] = useQuery(queries.personalSpaceOwners());
  const { memberUserIds } = useGroupMemberships();
  const displayName = useSpaceDisplayName();
  const canCreateSpace = useHasAccess("tenant:space_create");
  const [createOpen, setCreateOpen] = useState(false);

  const { q, show, manager, member } = useCurrentUrl(routes.settings.spaces);
  const search = q ?? "";
  const setSearch = (next: string) => {
    navigate(null, { q: next }, { replace: true });
  };
  const tab = isSpacesTab(show) ? show : "all";
  const managerFilter = manager ?? "all";
  const memberFilter = member ?? "all";

  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.settings.spaces,
    {
      name: "asc",
      members: "asc",
      workflows: "desc",
      chats: "desc",
      created: "desc",
    }
  );

  const usersById = useMemo(
    () => new Map(allUsers.map((u) => [u.id, u])),
    [allUsers]
  );
  const spacesById = useMemo(
    () => new Map(spaces.map((s) => [s.id, s])),
    [spaces]
  );
  const workflowCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const workflow of allWorkflows) {
      if (workflow.deletedAt != null) continue;
      counts.set(workflow.spaceId, (counts.get(workflow.spaceId) ?? 0) + 1);
    }
    return counts;
  }, [allWorkflows]);
  const ownerBySpaceId = useMemo(
    () =>
      new Map(
        personalSpaceOwners.flatMap((s) => {
          const owner = s.roleGrantsAsScope[0]?.user;
          return owner ? [[s.id, owner] as const] : [];
        })
      ),
    [personalSpaceOwners]
  );
  const chatCounts = useMemo(() => {
    const byUser = new Map<string, number>();
    for (const chat of allPersonalChats) {
      byUser.set(chat.userId, (byUser.get(chat.userId) ?? 0) + 1);
    }
    const bySpace = new Map<string, number>();
    for (const [spaceId, owner] of ownerBySpaceId) {
      bySpace.set(spaceId, byUser.get(owner.id) ?? 0);
    }
    return bySpace;
  }, [allPersonalChats, ownerBySpaceId]);

  const allRows = useMemo(
    () =>
      buildSpaceRows(
        spaces,
        memberUserIds,
        currentUser?.id ?? null,
        (space) => {
          if (space.mode === "personal") {
            const owner = ownerBySpaceId.get(space.id);
            if (owner) return userDisplayName(owner);
          }
          return displayName(space);
        },
        workflowCounts,
        chatCounts
      ),
    [
      spaces,
      memberUserIds,
      currentUser,
      displayName,
      ownerBySpaceId,
      workflowCounts,
      chatCounts,
    ]
  );

  const searchedRows = useMemo(
    () =>
      filterByMember(
        filterByManager(filterBySearch(allRows, search), managerFilter),
        memberFilter
      ),
    [allRows, search, managerFilter, memberFilter]
  );
  const tabCounts = useMemo(() => countByTab(searchedRows), [searchedRows]);
  const sortedRows = useMemo(
    () =>
      sortSpaceRows(filterByTab(searchedRows, tab), sortColumn, sortDirection),
    [searchedRows, tab, sortColumn, sortDirection]
  );

  const userOptions = useMemo(
    () =>
      allUsers
        .filter((u) => !u.deactivated)
        .map((u) => ({
          id: u.id,
          label: userDisplayName(u),
          menuLeft: <Avatar user={u} size={16} />,
          pillLeft: <Avatar user={u} size={12} />,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [allUsers]
  );

  const filters: FilterDef[] = [
    {
      kind: "single",
      key: "manager",
      label: "Managed by",
      icon: <Icon name="user-round-check" size={16} />,
      options: [
        ...userOptions,
        {
          id: "none",
          label: "No managers",
          menuLeft: <Icon name="circle-off" size={16} />,
          pillLeft: (
            <Icon name="circle-off" size={12} color="var(--text-secondary)" />
          ),
        },
      ],
      value: managerFilter === "all" ? null : managerFilter,
      defaultValue: "all",
      onSelect: (id) => navigate(null, { manager: id }, { replace: true }),
      onClear: () => navigate(null, { manager: "all" }, { replace: true }),
    },
    {
      kind: "single",
      key: "member",
      label: "Has member",
      icon: <Icon name="users-round" size={16} />,
      options: userOptions,
      value: memberFilter === "all" ? null : memberFilter,
      defaultValue: "all",
      onSelect: (id) => navigate(null, { member: id }, { replace: true }),
      onClear: () => navigate(null, { member: "all" }, { replace: true }),
    },
  ];

  const items = useMemo(() => {
    return sortedRows.map((row) => {
      const managerUsers = row.managerUserIds
        .map((id) => usersById.get(id))
        .filter((u) => u != null && !u.deactivated);
      const memberUsers = row.memberUserIds
        .map((id) => usersById.get(id))
        .filter((u) => u != null && !u.deactivated);
      const owner = row.mode === "personal" ? ownerBySpaceId.get(row.id) : null;
      return {
        key: row.id,
        link:
          row.mode === "personal"
            ? makeUrl(routes.space, { spaceId: row.id })
            : makeUrl(routes.modal.space, { id: row.id }),
        name: (
          <RowNameLayout>
            {owner ? (
              <Avatar user={owner} size={32} />
            ) : (
              <NeutralIconBadge
                iconName={
                  row.mode !== "personal" && !row.isMember
                    ? "discoverable-space"
                    : spaceIcon(row.mode)
                }
              />
            )}
            <RowNameContent>
              <Text
                ellipsis
                variant="text-regular-md"
                color="var(--text-primary)"
              >
                {row.name}
              </Text>
              <Text variant="text-small" color="var(--text-secondary)">
                {row.mode === "personal"
                  ? "Personal space and chat"
                  : memberCountLabel(row.memberCount)}
              </Text>
            </RowNameContent>
          </RowNameLayout>
        ),
        members:
          memberUsers.length > 0 ? (
            <GroupMemberFacePile
              members={memberUsers.map((user) => ({ user }))}
              totalCount={row.memberCount}
              size={24}
              onClickMember={(userId) =>
                navigate(routes.modal.member, { id: userId })
              }
            />
          ) : (
            <Text variant="text-small" color="var(--text-tertiary)">
              None
            </Text>
          ),
        managers:
          managerUsers.length > 0 ? (
            <GroupMemberFacePile
              members={managerUsers.map((user) => ({ user }))}
              totalCount={managerUsers.length}
              size={24}
              onClickMember={(userId) =>
                navigate(routes.modal.member, { id: userId })
              }
            />
          ) : (
            <Text variant="text-small" color="var(--text-tertiary)">
              None
            </Text>
          ),
        workflows: <Text variant="text-small">{row.workflowCount}</Text>,
        chats: <Text variant="text-small">{row.chatCount}</Text>,
        visibility:
          row.mode === "personal" ? (
            <Text variant="text-small">Personal</Text>
          ) : row.discoverable ? (
            <PillBadge icon="discoverable-space">Discoverable</PillBadge>
          ) : (
            <PillBadge icon="lock-filled">Invite only</PillBadge>
          ),
        created: (
          <Text variant="text-small">
            {new Date(row.createdAt).toLocaleDateString(undefined, {
              dateStyle: "medium",
            })}
          </Text>
        ),
        actions: (hasMouse: boolean) => {
          const space = spacesById.get(row.id);
          return space ? (
            <HoverActions visible={hasMouse}>
              <SpaceActionsMenu space={space} showSettings={false} />
            </HoverActions>
          ) : null;
        },
      };
    });
  }, [sortedRows, usersById, spacesById, ownerBySpaceId]);

  return (
    <Container>
      <TabHeaderBar>
        <Tabs>
          {TABS.map((t) => (
            <TabLink
              key={t.id}
              to={makeUrl(routes.settings.spaces, { show: t.id })}
              replace
              active={tab === t.id}
              icon={t.icon}
              label={t.label}
              count={spacesResult.type === "complete" ? tabCounts[t.id] : null}
            />
          ))}
        </Tabs>
        <HeaderActions>
          <SearchField
            placeholder="Search spaces…"
            value={search}
            onChange={setSearch}
          />
          <Tooltip
            content={
              !canCreateSpace
                ? "You don’t have permission to create spaces"
                : "Create space"
            }
          >
            <Button
              size="l"
              icon="plus"
              disabled={!canCreateSpace}
              onClick={() => setCreateOpen(true)}
            />
          </Tooltip>
        </HeaderActions>
      </TabHeaderBar>
      <ScrollArea>
        <ListTable
          headers={
            tab === "personal"
              ? {
                  name: "Name",
                  workflows: "Workflows",
                  chats: "Chats",
                  visibility: "Visibility",
                  created: "Created",
                  actions: "",
                }
              : {
                  name: "Name",
                  workflows: "Workflows",
                  visibility: "Visibility",
                  created: "Created",
                  members: "Members",
                  managers: "Managers",
                  actions: "",
                }
          }
          columns={{
            members: "members",
            managers: "members",
            visibility: "md",
          }}
          sortableColumns={sortableColumns}
          dateColumns={["created"]}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          headerActions={<ListFilter filters={filters} />}
          items={items}
          footer={
            hasActiveFilters(filters) ? (
              <ListFilterPills filters={filters} />
            ) : null
          }
          emptyState={
            spacesResult.type === "complete" ? (
              <EmptyState
                iconName="hash"
                title="No spaces"
                description="Try a different search or filter."
              />
            ) : null
          }
        />
      </ScrollArea>
      {createOpen && (
        <SpaceSettings
          mode="create"
          onClose={() => setCreateOpen(false)}
          onCreated={() => setCreateOpen(false)}
        />
      )}
    </Container>
  );
}
